import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { compact, groupBy, sortBy } from 'lodash';
import { Plus, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../components/AdditionalActionsMenu';
import { CardWithHover } from '../../../../components/CardWitHover';
import { ChipFilterWithSelect } from '../../../../components/ChipFilterWithDropdown';
import { DadoAIBanner } from '../../../../components/DadoAIBanner';
import { Loader } from '../../../../components/Loader';
import { TableSearch } from '../../../../components/Table';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useIsVisibleForRef } from '../../../../hooks/useIsVisible';
import { useScrollIntoView } from '../../../../hooks/useScrollIntoView';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { PageBody } from '../../../../layout/PageBody';
import { captureException } from '../../../../sentry';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useNewUx } from '../../../../services/newUx';
import { PageExperiencesOverviewClassic } from './OverviewClassic';
import {
  duplicateTemplate,
  getExperienceTemplateById
} from '../../../../services/experiences/template';
import { useExperienceTemplatesMinimalByOrgId } from '../../../../services/experiences/templateMinimal';
import { useInstanceStatsByTemplateId } from '../../../../services/experiences/instance';
import {
  usePeopleTabV2Enabled,
  useTimelineItemStatsByTemplateId
} from '../../../../services/experiences/timelineItemStatus';
import {
  getOrgIdOrFreeTierSampleContentOrgId,
  isFreeTierOrg
} from '../../../../services/organizationTier';
import {
  hasCurrentUserExperienceLevelScopes,
  hasUserLevelScopes
} from '../../../../services/permissions';
import { updateDoc } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { toSearchTermRegexp } from '../../../../services/user';
import { CopilotBadge } from '../../components/CopilotBadge';
import { TemplateStatusChip } from '../../components/TemplateStatusChip';

// Top-of-page status filter (next to search). "all" shows the active /
// drafts / inactive sections; archived is opt-in via its own filter value
// (so it stays out of the default view).
const STATUS_FILTERS = [
  { value: 'all', label: 'All experiences' },
  { value: 'active', label: 'Active' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' }
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

const ExperienceCard = styled(
  React.forwardRef<
    any,
    React.ComponentProps<typeof CardWithHover> & {
      highlighted?: boolean;
      noPointer?: boolean;
    }
  >(({ highlighted, noPointer, ...props }, ref) => (
    <CardWithHover {...props} ref={ref} />
  ))
)<{ highlighted?: boolean; noPointer?: boolean }>`
  background: ${(p) => p.theme.custom.colors.background.light};
  /* && beats the theme's MuiCard root padding (spacing(3) = 24px) which
     leaves the compact card feeling empty. */
  && {
    padding: ${(p) => p.theme.spacing(1.5)}px ${(p) => p.theme.spacing(2)}px;
    /* MuiCard-root clips overflow by default. Nothing here needs edge-to-edge
       clipping (no full-bleed image etc.), and the CopilotBadge's own gradient-border
       wrapper renders taller than its sibling status chip, which the compact padding
       above doesn't leave enough room for — clipping its corner against the card edge. */
    overflow: visible;
  }
  box-shadow: ${(p) =>
    p.highlighted
      ? `0 0 0 4px ${p.theme.custom.colors.green.w200}!important`
      : undefined};
  border-color: ${(p) =>
    p.highlighted
      ? `${p.theme.custom.colors.green.w300}!important`
      : undefined};

  /* Skip layout + paint for cards that are off-screen, and reserve their height
     so the scrollbar stays accurate and nothing shifts (no CLS). For a large org
     this is the main win: the browser only renders the handful of cards near the
     viewport on first paint instead of laying out and painting all of them, then
     renders the rest as they scroll in. The intrinsic-size "auto" keyword latches
     each card's real size after its first render, so the 152px is only an estimate
     for cards that have never been on-screen. Gracefully ignored on browsers
     without content-visibility. NEW_UX-only — OverviewClassic is untouched. */
  content-visibility: auto;
  contain-intrinsic-size: auto 152px;

  /* The card is no longer a link — View / Edit / ... do the navigating.
     Neutralize CardWithHover's pointer cursor + hover/active ring so the
     whole card doesn't read as clickable. The highlighted (scroll-into-
     view) ring uses !important above, so it survives these resets. */
  cursor: default;

  &:hover {
    box-shadow: none;
  }
  &:active {
    background-color: transparent;
    box-shadow: none;
  }
  pointer-events: ${(p) => (p.noPointer ? 'none' : 'auto')};
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  grid-row-gap: ${(p) => p.theme.spacing(3)}px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatusGroup = styled('div')`
  &:not(:last-of-type) {
    margin-bottom: ${(p) => p.theme.spacing(5)}px;
  }
`;

const StatusHeading = styled(Typography)`
  margin-bottom: ${(p) => p.theme.spacing(2)}px;
` as typeof Typography;

const CardHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.spacing()}px;
  margin-bottom: ${(p) => p.theme.spacing()}px;
`;

const ChipsRow = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing()}px;
  min-width: 0;
`;

const ParticipantCount = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(0.5)}px;
  flex-shrink: 0;
  font-size: ${(p) => p.theme.custom.fontSize.s}px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
`;

const CardTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
  height: calc(${(p) => p.theme.typography.h4.lineHeight} * 2);
` as typeof Typography;

// Meta line under the title. Content intentionally cleared (no task /
// automation / duration counts), but the row is kept and reserves one line of
// height so card heights stay stable and there's room to reintroduce meta later.
const MetaRow = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing()}px;
  min-height: ${(p) => p.theme.custom.fontSize.s * 1.5}px;
`;

const CardFooter = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.spacing()}px;
`;

const FooterButtons = styled('div')`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(1.5)}px;
`;

// New-theme accent for the primary action buttons on this surface.
const BLUE_600 = '#1D4ED8';

// size="small" keeps the original (good) height; min-width widens the buttons.
// !important beats the theme's MuiButton root `&&` { minWidth: unset } (doubled
// specificity) which otherwise lets the buttons shrink to their label width.
// && beats the theme's MuiButton-outlinedPrimary color/border so the primary
// (View) button reads as blue600 rather than the app's default primary blue.
const NavButtonBase = styled(Button)`
  min-width: 104px !important;
  && {
    color: ${BLUE_600};
    border-color: ${BLUE_600};
  }
  &&:hover {
    border-color: ${BLUE_600};
    background-color: rgba(29, 78, 216, 0.04);
  }
` as typeof Button;

// Edit uses a neutral gray outline to visually distinguish it from the primary
// (blue) View button, while keeping the outlined style. && beats the theme's
// MuiButton-outlinedPrimary color/border.
const NavButtonNeutral = styled(Button)`
  min-width: 104px !important;
  && {
    color: ${(p) => p.theme.custom.colors.gray.w500};
    border-color: ${(p) => p.theme.custom.colors.gray.w300};
  }
  &&:hover {
    border-color: ${(p) => p.theme.custom.colors.gray.w500};
    background-color: transparent;
  }
` as typeof Button;

// View/Edit nav button. When linkable, wraps the button in a react-router Link;
// for free-tier orgs (not linkable) renders a plain disabled button so it
// genuinely can't navigate.
const NavButton: React.FC<
  React.PropsWithChildren<{
    to: string;
    linkable: boolean;
    tone?: 'primary' | 'neutral';
  }>
> = ({ to, linkable, tone = 'primary', children }) => {
  const Base = tone === 'neutral' ? NavButtonNeutral : NavButtonBase;
  return linkable ? (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Base variant="outlined" color="primary" size="small">
        {children}
      </Base>
    </Link>
  ) : (
    <Base variant="outlined" color="primary" size="small" disabled>
      {children}
    </Base>
  );
};

type EnrollStatsShape = { todo: number; overdue: number; done: number };

// enrolled = todo + overdue + done (mirrors the count the old dense TaskTimeline
// surfaced).
const EnrolledCountView: React.FC<{
  stats: EnrollStatsShape | undefined | void;
}> = ({ stats }) => {
  if (!stats) {
    return null;
  }
  const enrolled = stats.todo + stats.overdue + stats.done;
  return (
    <ParticipantCount title={`${enrolled} enrolled`}>
      <Users size={15} />
      {enrolled}
    </ParticipantCount>
  );
};

// V1 = full experiencesV1 docs; V2 = the compact timelineItemStatusV1 projection.
// Hooks can't be called conditionally, so the flag switch happens at the
// component boundary (mirrors EnrollStats in Details/TaskTimeline.tsx). Own
// component so the per-template Firestore listener mounts only for cards that
// show it (non-draft). Both hooks read only orgId + id + expectedEndThreshold,
// all present on the field-masked minimal template doc.
const EnrolledCountV1: React.FC<{ tpl: Doc<IExperienceTemplate> }> = ({
  tpl
}) => {
  const [stats] = useInstanceStatsByTemplateId(
    tpl.data.orgId,
    tpl.id,
    tpl.data.expectedEndThreshold
  );
  return <EnrolledCountView stats={stats} />;
};

const EnrolledCountV2: React.FC<{ tpl: Doc<IExperienceTemplate> }> = ({
  tpl
}) => {
  const [stats] = useTimelineItemStatsByTemplateId(
    tpl.data.orgId,
    tpl.id,
    tpl.data.expectedEndThreshold
  );
  return <EnrolledCountView stats={stats} />;
};

const EnrolledCount: React.FC<{ tpl: Doc<IExperienceTemplate> }> = ({ tpl }) =>
  usePeopleTabV2Enabled() ? (
    <EnrolledCountV2 tpl={tpl} />
  ) : (
    <EnrolledCountV1 tpl={tpl} />
  );

// True only AFTER the browser has painted at least one frame following this
// card's mount. The per-template count opens Firestore listener(s) whose
// currently-relevant doc stream can be large on a heavy org (thousands of
// projection docs); deserializing that flood is a multi-second main-thread
// burst. Now that the card list itself loads fast (field-masked REST read), the
// counts no longer gate the grid's first paint — but we still defer them so the
// burst runs AFTER the cards are on screen rather than competing with them.
// Cards paint first; counts fill in a beat later. Double rAF = "after the next
// paint"; the idle callback then yields so we don't fight that paint. Re-arms
// every mount, so it applies on each visit to the Overview.
const useAfterFirstPaint = (): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let idleId: number | undefined;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const scheduleIdle = (cb: () => void): number =>
      typeof w.requestIdleCallback === 'function'
        ? w.requestIdleCallback(cb, { timeout: 500 })
        : (window.setTimeout(cb, 32) as unknown as number);
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        idleId = scheduleIdle(() => setReady(true));
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (idleId !== undefined) {
        if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(idleId);
        else window.clearTimeout(idleId);
      }
    };
  }, []);
  return ready;
};

const ExperienceItem = ({
  tpl,
  linkTo,
  anchorId,
  scrollIntoView,
  onDuplicate,
  onChanged,
  canCreate
}: {
  tpl: Doc<IExperienceTemplate>;
  linkTo: string;
  anchorId: string;
  scrollIntoView: boolean;
  onDuplicate: (d: Doc<IExperienceTemplate>) => void;
  // Refetch the (one-shot) minimal list after a mutation so the grid reflects it.
  onChanged: () => void;
  canCreate: boolean;
}) => {
  const ref = useScrollIntoView(scrollIntoView, { behavior: 'smooth' });
  // Lazy-mount the per-template enrolled-count listener only once the card
  // scrolls near the viewport, then latch it on — keeps the initial fan-out to
  // roughly the cards on screen. Falls back to eager mount where
  // IntersectionObserver is unavailable.
  const { isVisible } = useIsVisibleForRef(ref, { rootMargin: '100px 0px' });
  const [visibleLatched, setVisibleLatched] = useState(
    typeof IntersectionObserver === 'undefined'
  );
  useEffect(() => {
    if (isVisible) {
      setVisibleLatched(true);
    }
  }, [isVisible]);
  // Mount the count only once the card is on-screen AND the grid has painted at
  // least once, so the count listeners' doc-stream burst runs behind the
  // already-visible cards instead of competing with their first paint.
  const afterFirstPaint = useAfterFirstPaint();
  const countMounted = visibleLatched && afterFirstPaint;
  const { id, org } = useCurrentUser();
  const { ROUTES, goTo } = useRoutes();
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const options: AdditionActionsMenuOption[] = useMemo(
    () =>
      compact([
        tpl.data.status === ExperienceTemplateStatus.ACTIVE && {
          key: 'stop',
          label: t('Stop'),
          onClick: () =>
            updateDoc(tpl, () => ({
              status: ExperienceTemplateStatus.INACTIVE
            }))
              .then(() => {
                enqueueSnackbarSuccess(t('Experience stopped'));
                onChanged();
              })
              .catch((err) => {
                captureException(err, {
                  tags: { action: 'stop_experience_template' },
                  extra: { templateId: tpl.id }
                });
                enqueueSnackbarError(t('Failed to stop experience'));
              })
        },
        tpl.data.status !== ExperienceTemplateStatus.ACTIVE &&
          tpl.data.status !== ExperienceTemplateStatus.ARCHIVED && {
            key: 'archive',
            label: t('Archive'),
            onClick: () =>
              updateDoc(tpl, () => ({
                status: ExperienceTemplateStatus.ARCHIVED
              }))
                .then(() => {
                  enqueueSnackbarSuccess(t('Experience archived'));
                  onChanged();
                })
                .catch((err) => {
                  captureException(err, {
                    tags: { action: 'archive_experience_template' },
                    extra: { templateId: tpl.id }
                  });
                  enqueueSnackbarError(t('Failed to archive experience'));
                })
          },
        canCreate && {
          key: 'duplicate',
          label: 'Duplicate',
          // The overview lists a field-masked (minimal) template, so the full
          // authoring payload duplicateTemplate needs isn't present on tpl.data.
          // Lazy-load the full doc by id on click, then duplicate.
          onClick: () =>
            getExperienceTemplateById(tpl.id)
              .then((full) => {
                if (!full) throw new Error('TEMPLATE_NOT_FOUND');
                return duplicateTemplate(full.data, id);
              })
              .then((d) => {
                onDuplicate(d);
                enqueueSnackbarSuccess(
                  t(`Created new draft based on '${tpl.data.title}'`)
                );
                onChanged();
              })
              .catch((err) => {
                captureException(err, {
                  tags: { action: 'duplicate_experience_template' },
                  extra: { templateId: tpl.id }
                });
                enqueueSnackbarError(t('Failed to duplicate experience'));
              })
        }
      ]),
    [
      tpl,
      id,
      onDuplicate,
      onChanged,
      enqueueSnackbarSuccess,
      enqueueSnackbarError,
      canCreate,
      t
    ]
  );

  // Free-tier orgs can't open experiences — disable the nav buttons.
  const linkable = !isFreeTierOrg(org);
  // Match the Details page's Edit button: active experiences jump straight to
  // the timeline (tasks) step; everything else opens the wizard at its start.
  const editUrl =
    tpl.data.status === ExperienceTemplateStatus.ACTIVE
      ? ROUTES.experiences.details.edit.url(tpl.id, { step: 'tasks' })
      : ROUTES.experiences.details.edit.url(tpl.id);
  const showCount =
    tpl.data.status !== ExperienceTemplateStatus.DRAFT &&
    tpl.data.status !== ExperienceTemplateStatus.ROUGH_DRAFT;

  return (
    <ExperienceCard
      variant="outlined"
      ref={ref}
      id={anchorId}
      data-highlighted={scrollIntoView}
      onClick={linkable ? () => goTo(linkTo) : undefined}
    >
      <CardHeader>
        <ChipsRow>
          <TemplateStatusChip status={tpl.data.status} size="small" />
          {tpl.data.aiFeatureUsed && <CopilotBadge size="small" />}
        </ChipsRow>
        {showCount && countMounted && <EnrolledCount tpl={tpl} />}
      </CardHeader>

      <CardTitle variant="h4" component="h4">
        {tpl.data.adminTitle || tpl.data.title}
      </CardTitle>

      <MetaRow />

      <CardFooter>
        <FooterButtons>
          <NavButton to={linkTo} linkable={linkable}>
            {t('View')}
          </NavButton>
          <div onClick={(ev) => ev.stopPropagation()}>
            <NavButton to={editUrl} linkable={linkable}>
              {t('Edit')}
            </NavButton>
          </div>
        </FooterButtons>
        <div onClick={(ev) => ev.stopPropagation()}>
          <AdditionalActionsMenu options={options} variant="horizontal" />
        </div>
      </CardFooter>
    </ExperienceCard>
  );
};

const EmptyStateContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: 400,

  '&&': {
    '> *': {
      marginBottom: p.theme.spacing(2),
      textAlign: 'center'
    }
  }
}));

const EmptyState = ({
  canCreate,
  hasAnyTemplatesButNonAccessible
}: {
  canCreate: boolean;
  hasAnyTemplatesButNonAccessible: boolean;
}) => {
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();
  return (
    <PageBody noTopPadding>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography variant="h1" component="h1">
            {t('Experiences')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          {isBrowser && canCreate && (
            <Link to={ROUTES.experiences.details.create.url()}>
              <Button
                startIcon={<Plus size={16} />}
                variant="contained"
                color="primary"
                fullWidth
              >
                {t('New Experience')}
              </Button>
            </Link>
          )}
        </PageToolbarActions>
      </PageToolbar>
      <EmptyStateContainer>
        <Typography variant="h2" color="primary">
          {hasAnyTemplatesButNonAccessible
            ? t(`You currently don't have access to any experiences.`)
            : t('Your organization doesn’t have any experiences yet')}
        </Typography>
        {canCreate && (
          <Typography variant="body1">
            {t(
              'To create a new experience, click on the button on the top right of the screen'
            )}
          </Typography>
        )}
        {!canCreate && hasAnyTemplatesButNonAccessible && (
          <Typography variant="body1">
            {t('Please contact your account owner to get access.')}
          </Typography>
        )}
      </EmptyStateContainer>
    </PageBody>
  );
};

/**
 * NEW_UX gate for the Experiences overview (the de-facto homepage). Renders the new
 * overhaul overview when NEW_UX is on, or the recovered pre-SC-31250 page when off.
 * A thin wrapper (not an early return inside the body) so each variant calls its own
 * hooks unconditionally — flipping the flag swaps components without breaking the
 * rules of hooks. Every later overhaul surface gates the same way via useNewUx().
 */
export const PageExperiencesOverview = () => {
  const { enabled } = useNewUx();
  return enabled ? (
    <PageExperiencesOverviewNew />
  ) : (
    <PageExperiencesOverviewClassic />
  );
};

const PageExperiencesOverviewNew = () => {
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const { ROUTES, getHash, updateHash } = useRoutes();
  const [tpls, loading, , reloadTpls] = useExperienceTemplatesMinimalByOrgId(
    getOrgIdOrFreeTierSampleContentOrgId(org)
  );
  const { t } = useTranslation();
  const hash = getHash();

  const [q, setQ] = useState('');

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { trackEvent } = useProductAnalytics();
  useEffect(() => {
    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Experiences List Page Loaded',
      Explanation: 'Admin user loads the page listing all experiences'
    });
  }, [trackEvent]);

  const accessibleTpls = useMemo(() => {
    if (!tpls) {
      return null;
    }
    return tpls.filter((tpl) =>
      hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
    );
  }, [tpls, currentUser]);

  const grouped = useMemo(() => {
    if (!accessibleTpls) {
      return null;
    }
    const re = toSearchTermRegexp(q);
    return groupBy(
      sortBy(
        accessibleTpls.filter((tpl) =>
          (tpl.data.adminTitle || tpl.data.title).match(re)
        ),
        (e) => -e.data.updatedAt.toMillis()
      ),
      (e) => e.data.status
    );
  }, [accessibleTpls, q]);

  if (loading) {
    return <Loader height={500} />;
  }

  const canCreate = hasUserLevelScopes(currentUser, [
    'experience.template.create'
  ]);

  if (tpls && !tpls.length) {
    return (
      <EmptyState
        canCreate={canCreate}
        hasAnyTemplatesButNonAccessible={false}
      />
    );
  }

  if (tpls && tpls.length && accessibleTpls && !accessibleTpls.length) {
    return (
      <EmptyState
        canCreate={canCreate}
        hasAnyTemplatesButNonAccessible={true}
      />
    );
  }

  if (grouped) {
    const active = grouped[ExperienceTemplateStatus.ACTIVE] || [];
    const inactive = grouped[ExperienceTemplateStatus.INACTIVE] || [];
    const drafts = grouped[ExperienceTemplateStatus.DRAFT] || [];
    const roughDrafts = grouped[ExperienceTemplateStatus.ROUGH_DRAFT] || [];
    const archived = grouped[ExperienceTemplateStatus.ARCHIVED] || [];
    // Drafts + rough drafts share the "Drafts" section.
    const allDrafts = [...drafts, ...roughDrafts];

    const renderItem = (exp: Doc<IExperienceTemplate>) => (
      <ExperienceItem
        key={exp.id}
        tpl={exp}
        // View → details/general on every card; Edit → edit (computed in
        // ExperienceItem). Both buttons show on all cards.
        linkTo={ROUTES.experiences.details.general.url(exp.id)}
        anchorId={`tpl-${exp.id}`}
        scrollIntoView={hash.slice(1) === `tpl-${exp.id}`}
        onDuplicate={(d) => updateHash(`tpl-${d.id}`)}
        onChanged={reloadTpls}
        canCreate={canCreate}
      />
    );

    const renderGroup = (title: string, items: Doc<IExperienceTemplate>[]) =>
      items.length ? (
        <StatusGroup>
          <StatusHeading variant="h2" component="h2">
            {title}
          </StatusHeading>
          <Grid>{items.map(renderItem)}</Grid>
        </StatusGroup>
      ) : null;

    return (
      <>
        <DadoAIBanner />
        <PageBody noTopPadding>
          <TeaserBoxFreeTierForExperiences>
            {t(
              'This is a preview of Experiences, a feature which enables you to run personalized, automated HR processes at scale.'
            )}
          </TeaserBoxFreeTierForExperiences>
          <PageToolbar sticky>
            {/* No title now — push the controls to the right edge */}
            <PageToolbarActions style={{ marginLeft: 'auto' }}>
              <ChipFilterWithSelect
                size="medium"
                color="#fff"
                backgroundColor={BLUE_600}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
                options={STATUS_FILTERS.map((f) => ({
                  value: f.value,
                  label: t(f.label)
                }))}
              />
              <TableSearch
                term={q}
                onChange={setQ}
                autoFocus
                placeholder={t('Search by name')}
              />
              {isBrowser && canCreate && (
                <Link to={ROUTES.experiences.details.create.url()}>
                  <Button
                    startIcon={<Plus size={16} />}
                    variant="contained"
                    color="primary"
                    fullWidth
                    style={{ backgroundColor: BLUE_600, color: '#fff' }}
                  >
                    {t('New Experience')}
                  </Button>
                </Link>
              )}
            </PageToolbarActions>
          </PageToolbar>
          <Section>
            {(statusFilter === 'all' || statusFilter === 'active') &&
              renderGroup(t('Active'), active)}
            {(statusFilter === 'all' || statusFilter === 'drafts') &&
              renderGroup(t('Drafts'), allDrafts)}
            {(statusFilter === 'all' || statusFilter === 'inactive') &&
              renderGroup(t('Inactive'), inactive)}
            {statusFilter === 'archived' && renderGroup(t('Archived'), archived)}
          </Section>
        </PageBody>
      </>
    );
  }

  return null;
};
